import { Flame } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getUserStats, getStreakColor, getStreakEmoji } from '../lib/gamification';
import type { UserStats } from '../lib/supabase';

export function StreakDisplay() {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [streakColor, setStreakColor] = useState('from-orange-300 to-orange-400');
  const [emoji, setEmoji] = useState('✨');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    const userStats = await getUserStats();
    setStats(userStats);

    if (userStats) {
      const color = await getStreakColor(userStats.current_streak_days);
      const streakEmoji = await getStreakEmoji(userStats.current_streak_days);
      setStreakColor(color);
      setEmoji(streakEmoji);
    }
  };

  if (!stats || stats.current_streak_days === 0) {
    return null;
  }

  const getMilestoneMessage = (days: number): string => {
    if (days >= 365) return 'Legendary streak!';
    if (days >= 90) return 'Unstoppable!';
    if (days >= 30) return 'On fire!';
    if (days >= 7) return 'Great momentum!';
    return 'Keep going!';
  };

  return (
    <div className={`bg-gradient-to-r ${streakColor} rounded-2xl p-4 mb-4 text-white shadow-lg animate-glow-pulse`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white bg-opacity-20 rounded-full p-3 animate-streak-pulse backdrop-blur-sm">
            <Flame className="text-white" size={28} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold">{stats.current_streak_days}</span>
              <span className="text-lg opacity-90">day{stats.current_streak_days !== 1 ? 's' : ''}</span>
              <span className="text-2xl animate-bounce-in">{emoji}</span>
            </div>
            <p className="text-sm opacity-90 font-medium">{getMilestoneMessage(stats.current_streak_days)}</p>
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs opacity-75 uppercase tracking-wide mb-1">Best</div>
          <div className="text-2xl font-bold">{stats.longest_streak_days}</div>
        </div>
      </div>

      <div className="mt-3 flex gap-1">
        {[7, 14, 30, 60, 90, 180, 365].map((milestone) => {
          const reached = stats.current_streak_days >= milestone;
          return (
            <div
              key={milestone}
              className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
                reached ? 'bg-white' : 'bg-white bg-opacity-20'
              }`}
              title={`${milestone} days`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-xs opacity-75">
        <span>7d</span>
        <span>1yr</span>
      </div>
    </div>
  );
}
